import { useCallback, useEffect, useState } from 'react';
import {
  HASH_STEP_MAP,
  PET_DEMOS_STEPS,
  STEP_HASH_MAP,
} from '../petDemographics.constants';

/**
 * Returns the URL hash string for a given step number.
 * @param {number} step - A PET_DEMOS_STEPS value (e.g. PET_DEMOS_STEPS.WELCOME)
 * @returns {string} Hash with leading #, e.g. "#pd-welcome". Falls back to
 *   the WELCOME hash if the step has no mapping.
 */
const getHashForStep = (step) => `#${STEP_HASH_MAP[step]
  || STEP_HASH_MAP[PET_DEMOS_STEPS.WELCOME]}`;

/**
 * Reads the current URL hash and returns the matching step number.
 * @returns {number} A PET_DEMOS_STEPS value derived from window.location.hash,
 *   or PET_DEMOS_STEPS.WELCOME if the hash is absent or unrecognized.
 */
const getStepFromHash = () => {
  const hash = window.location.hash.replace('#', '');
  return HASH_STEP_MAP[hash] ?? PET_DEMOS_STEPS.WELCOME;
};

// Static map avoids recreating goToNextStep callback on every step change
const STEP_SEQUENCE = {
  [PET_DEMOS_STEPS.WELCOME]: PET_DEMOS_STEPS.YOUR_POLICY,
  [PET_DEMOS_STEPS.YOUR_POLICY]: PET_DEMOS_STEPS.COMPLETE_PET_DETAILS,
  [PET_DEMOS_STEPS.COMPLETE_PET_DETAILS]: PET_DEMOS_STEPS.COMPLETE_PET_REMOVAL,
  [PET_DEMOS_STEPS.COMPLETE_PET_REMOVAL]: PET_DEMOS_STEPS.INELIGIBLE_PETS,
  [PET_DEMOS_STEPS.INELIGIBLE_PETS]: PET_DEMOS_STEPS.YOUR_POLICY,
};

const usePetDemosSteps = () => {
  const [currentStep, setCurrentStep] = useState(getStepFromHash);

  // On mount: push the initial hash if the URL has no valid pet-demos hash.
  // On unmount: remove the hash so it doesn't persist on unrelated pages.
  useEffect(() => {
    const currentHash = window.location.hash.replace('#', '');
    const isValidPetDemosHash = !!HASH_STEP_MAP[currentHash];

    if (!isValidPetDemosHash) {
      const welcomeHash = getHashForStep(PET_DEMOS_STEPS.WELCOME);
      window.history.pushState(null, '', welcomeHash);
    }

    return () => {
      window.history.replaceState(null, '', window.location.pathname);
    };
  }, []);

  // Sync React state with the browser's native back/forward navigation.
  // Read the new hash and update currentStep.
  useEffect(() => {
    const handlePopState = () => setCurrentStep(getStepFromHash());
    window.addEventListener('popstate', handlePopState);

    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const goToSpecificStep = useCallback((nextStep) => {
    window.history.pushState(null, '', getHashForStep(nextStep));
    setCurrentStep(nextStep);
  }, []);

  const goToNextStep = useCallback(() => {
    goToSpecificStep(STEP_SEQUENCE[currentStep] ?? PET_DEMOS_STEPS.WELCOME);
  }, [currentStep, goToSpecificStep]);

  // Delegates to the browser's history stack instead of managing our own.
  const goToPreviousStep = useCallback(() => {
    window.history.back();
  }, []);

  // Validate if coverage period is completed based on period completion data
  // This function should be called with coveragePeriodPetsData and groupId
  const isCoveragePeriodCompleted = useCallback((
    groupId,
    coveragePeriodPetsData,
  ) => {
    if (!groupId || !coveragePeriodPetsData) {
      return false;
    }

    const petsData = coveragePeriodPetsData.get(groupId);
    if (!petsData) {
      return false;
    }

    // Check if this period has completion flags stored
    // These are set when steps are completed for this specific period
    const hasCompletedStep3 = petsData.hasCompletedPetDetails;
    const hasCompletedStep6 = petsData.hasCompletedPetRemoval;
    // Step 6 is only required when there's a removal date the user still
    // needs to resolve manually (not just when removedPets is non-empty,
    // since rollover can auto-populate removals with nothing left for
    // the user to do on the removal screen). Older persisted data (saved
    // before this flag existed) won't have it, so fall back to the old
    // "were any pets removed" check in that case.
    const needsStep6 = petsData.needsPetRemovalStep
      ?? (petsData.removedPets?.length > 0);
    const step6Satisfied = !needsStep6 || hasCompletedStep6;

    const hasCompletedStep7 = petsData.hasCompletedIneligiblePets;
    // Step 7 is only required if there are ineligible pets
    const hasIneligiblePets = petsData.ineligiblePets?.length > 0;
    const needsStep7 = hasIneligiblePets;
    const step7Satisfied = !needsStep7 || hasCompletedStep7;

    const eligiblePets = petsData.eligiblePets || [];
    const allEligiblePetsComplete = eligiblePets.every((pet) => {
      const { petName, dateOfBirth, breed, weight } = pet.demographicData || {};
      return petName?.trim() && dateOfBirth && breed
        && weight && Number(weight) > 0;
    });

    return hasCompletedStep3 && step6Satisfied && step7Satisfied
      && allEligiblePetsComplete;
  }, []);

  useEffect(() => {
    // scroll to top every step change (for mobile devices with vertical scroll)
    window.scrollTo({
      behavior: 'smooth',
      top: 0,
    });
  }, [currentStep]);

  return {
    currentStep,
    goToNextStep,
    goToPreviousStep,
    goToSpecificStep,
    initialStep: currentStep === PET_DEMOS_STEPS.WELCOME,
    isCoveragePeriodCompleted,
  };
};

export { usePetDemosSteps };
